import { motion } from "motion/react";
import { Link } from "react-router-dom";
import { BadgeEuro, FileSearch, MessagesSquare } from "lucide-react";
import { Compass } from "./Compass";
import { EASE_EXPO } from "@/components/motion/primitives";

const INK = "#0A0F0E";
const BRAND_DARK = "#00332F";
const TEAL = "#2EE6D6";
const SECONDARY = "#46504D";
const PANEL = "#F2F2ED";

/*
 * Wise's "Disappoint thieves", for our students: the people who prey on them
 * are fake agents selling "guaranteed" visas. What protects students is
 * knowing they will never be asked to pay, being able to check every figure
 * themselves, and having somewhere to ask before they pay anyone.
 * Every line must stay true.
 */
const FACTS = [
  { Icon: BadgeEuro, text: "We never ask you to pay for a visa, a slot or a guarantee" },
  { Icon: FileSearch, text: "Every figure comes from an official Austrian source you can check yourself" },
  { Icon: MessagesSquare, text: "Not sure about an offer? Ask the community before you pay anyone" },
];

export function TrustSection() {
  return (
    <section className="bg-white pb-6 pt-16 sm:pb-8 sm:pt-24">
      <div className="mx-auto max-w-[1180px] px-5 sm:px-8">
        <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-[minmax(0,1fr)_auto] lg:gap-16">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, ease: EASE_EXPO }}
          >
            <h2 className="font-black leading-[0.98] tracking-[-0.045em]" style={{ color: INK, fontSize: "clamp(2.6rem, 1.6rem + 4vw, 5.2rem)" }}>
              Disappoint the scammers
            </h2>
            <p className="mt-6 max-w-[34rem] text-[18px] leading-relaxed sm:text-[21px]" style={{ color: SECONDARY }}>
              Fake agents sell “guaranteed” visas to students every year. We never charge you and never promise what only Austria decides.
            </p>
            <Link
              to="/community/guaranteed-visa-offer"
              className="mt-8 inline-flex rounded-full px-8 py-4 text-[17px] font-bold transition-transform hover:-translate-y-0.5"
              style={{ backgroundColor: TEAL, color: BRAND_DARK }}
            >
              How to spot a fake offer
            </Link>
          </motion.div>
          <div className="flex justify-center py-4 lg:justify-end lg:pr-4">
            <Compass />
          </div>
        </div>

        <ul className="mt-14 grid grid-cols-1 gap-8 sm:mt-20 md:grid-cols-3 md:gap-10">
          {FACTS.map(({ Icon, text }, i) => (
            <motion.li
              key={text}
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.7, delay: i * 0.08, ease: EASE_EXPO }}
            >
              <span className="flex h-16 w-16 items-center justify-center rounded-full" style={{ backgroundColor: PANEL, color: INK }}>
                <Icon className="h-6 w-6" strokeWidth={2} />
              </span>
              <p className="mt-5 max-w-[22rem] text-[19px] font-semibold leading-snug sm:text-[20px]" style={{ color: SECONDARY }}>
                {text}
              </p>
            </motion.li>
          ))}
        </ul>
      </div>
    </section>
  );
}
